import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, setToken, getToken, formatApiErrorDetail } from "../lib/game";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = logged in
  const [authReady, setAuthReady] = useState(false);
  const processedHash = useRef(false);

  const finishAuth = useCallback((data) => {
    setToken(data.token);
    setUser(data.user);
  }, []);

  useEffect(() => {
    (async () => {
      // Handle Emergent Google OAuth callback (session_id in URL fragment)
      const hash = window.location.hash || "";
      if (hash.includes("session_id=") && !processedHash.current) {
        processedHash.current = true;
        const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
        try {
          const { data } = await api.post("/auth/google", { session_id: sid });
          finishAuth(data);
        } catch (e) {
          setUser(false);
        } finally {
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          setAuthReady(true);
        }
        return;
      }
      // Otherwise resume an existing session token
      if (getToken()) {
        try {
          const { data } = await api.get("/auth/me");
          setUser(data);
        } catch (e) {
          setToken(null);
          setUser(false);
        }
      } else {
        setUser(false);
      }
      setAuthReady(true);
    })();
  }, [finishAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      finishAuth(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      finishAuth(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: newPassword });
      finishAuth(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* noop */
    }
    setToken(null);
    setUser(false);
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      /* noop */
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, authReady, login, register, loginGoogle, logout, forgotPassword, resetPassword, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
